import { useEffect, useState, useCallback } from 'react';
import { IScript, IScriptExecutionResult } from '@skystone/shared';
import { scriptApi } from '@/services/script-api';
import { Button, Spinner, Badge, PageHeader, PageLoading, ErrorBanner } from '@/components/ui';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { PageContainer } from '@/components/layout';
import { getErrorMessage } from '@/lib/utils';

interface ScriptFormState {
  [scriptId: string]: Record<string, string>;
}

export function AdminScriptsPage() {
  const [scripts, setScripts] = useState<IScript[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [executing, setExecuting] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, IScriptExecutionResult>>({});
  const [formState, setFormState] = useState<ScriptFormState>({});

  const loadScripts = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await scriptApi.getScripts();
      setScripts(data);

      // Initialize form state with default values
      const initialState: ScriptFormState = {};
      for (const script of data) {
        initialState[script.id] = {};
        if (script.parameters) {
          for (const param of script.parameters) {
            initialState[script.id][param.name] = param.defaultValue ?? '';
          }
        }
      }
      setFormState(initialState);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load scripts'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadScripts();
  }, [loadScripts]);

  const handleParamChange = (scriptId: string, paramName: string, value: string): void => {
    setFormState((prev) => ({
      ...prev,
      [scriptId]: {
        ...prev[scriptId],
        [paramName]: value,
      },
    }));
  };

  const handleExecute = async (scriptId: string): Promise<void> => {
    setExecuting(scriptId);
    setResults((prev) => {
      const next = { ...prev };
      delete next[scriptId];
      return next;
    });

    try {
      const result = await scriptApi.execute(scriptId, formState[scriptId]);
      setResults((prev) => ({ ...prev, [scriptId]: result }));
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Execution failed');
      setResults((prev) => ({
        ...prev,
        [scriptId]: {
          scriptId,
          success: false,
          message,
          durationMs: 0,
        },
      }));
    } finally {
      setExecuting(null);
    }
  };

  if (loading) {
    return <PageLoading />;
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorBanner message={error} />
      </div>
    );
  }

  return (
    <PageContainer variant="wide" className="space-y-6 p-6">
      <PageHeader
        title="Scripts"
        subtitle="Run administrative scripts directly from the browser."
      />

      <div className="grid gap-4">
        {scripts.map((script) => {
          const result = results[script.id];
          const isExecuting = executing === script.id;

          return (
            <Card key={script.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{script.name}</CardTitle>
                    <CardDescription>{script.description}</CardDescription>
                  </div>
                  <Button
                    onClick={() => handleExecute(script.id)}
                    disabled={isExecuting || executing !== null}
                  >
                    {isExecuting ? (
                      <span className="flex items-center gap-2">
                        <Spinner /> Running...
                      </span>
                    ) : (
                      'Run'
                    )}
                  </Button>
                </div>
              </CardHeader>

              <CardContent>
                {/* Parameters */}
                {script.parameters && script.parameters.length > 0 && (
                  <div className="mb-4 space-y-3">
                    {script.parameters.map((param) => (
                      <div key={param.name} className="flex items-center gap-3">
                        <label
                          htmlFor={`${script.id}-${param.name}`}
                          className="w-32 text-sm font-medium"
                        >
                          {param.label}
                        </label>
                        <input
                          id={`${script.id}-${param.name}`}
                          type="text"
                          value={formState[script.id]?.[param.name] ?? ''}
                          onChange={(e) =>
                            handleParamChange(script.id, param.name, e.target.value)
                          }
                          disabled={isExecuting}
                          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Result */}
                {result && (
                  <div
                    className={`rounded-md p-4 ${
                      result.success
                        ? 'bg-success/10 text-success'
                        : 'bg-error/10 text-error'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={result.success ? 'success' : 'error'}>
                        {result.success ? 'Success' : 'Failed'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {result.durationMs > 0 && `${(result.durationMs / 1000).toFixed(1)}s`}
                      </span>
                    </div>
                    <p className="text-sm">{result.message}</p>

                    {result.details && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                          Details
                        </summary>
                        <pre className="mt-1 overflow-auto rounded bg-muted p-2 text-xs text-foreground">
                          {JSON.stringify(result.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </PageContainer>
  );
}
